import { useRef, useState } from 'react';
import { Stack } from '@adc/parallax-component-library';

import { ContentLoader } from '~/components/ContentLoader';
import { PatientInformationForm } from '~/components/forms/PatientInformationForm';
import { PatientIntroForm } from '~/components/forms/PatientIntroForm';
import { PatientSettingsForm } from '~/components/forms/patientSettings/PatientSettingsForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import { PatientCreateSteps } from '~/features/patientCreation/constants';
import type {
  PatientCreationFormState,
  PatientInformationRef,
  PatientIntroFormRef,
  PatientSettingsFormRef,
} from '~/features/patientCreation/types';
import { usePatientCreationForm } from '~/features/patientCreation/usePatientCreationForm';
import { buildTestId } from '~/utils/buildTestId';

import { FoundPatientMessage } from './FoundPatientMessage';

const getModalTitle = (currentStep: PatientCreationFormState['step']) => {
  switch (currentStep) {
    case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
      return 'Patient already exists in this department';
    case PatientCreateSteps.PATIENT_SETTINGS:
      return 'Patient App Data Display and Alert Settings';
    case PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND:
      return 'Transfer patient to this department?';
    case PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND:
      return 'Patient is archived';
    default:
      return 'Add Patient';
  }
};

const TEST_ID = 'patient-create' as const;

type Props = {
  onViewPatientProfile: (patientId: string) => Promise<void>;
};

export const PatientCreateModal = ({ onViewPatientProfile }: Props) => {
  const introFormRef = useRef<PatientIntroFormRef>(null);
  const settingsFormRef = useRef<PatientSettingsFormRef>(null);
  const informationFormRef = useRef<PatientInformationRef>(null);
  const [exitConfirmation, setExitConfirmation] = useState(false);
  const { openModal, closeModal } = useModal();

  const {
    currentStep,
    onModalClose,
    goToNextStep,
    isNextStepDisabled,
    goToPreviousStep,
    isFormLoading,
    onPatientIntroSubmit,
    setIsNextStepDisabled,
    onPatientInformationSubmit,
    values,
    foundPatient,
    getNextStepButtonLabel,
    resetForm,
  } = usePatientCreationForm({
    onViewPatientProfile,
    introFormRef,
    settingsFormRef,
    informationFormRef,
  });

  const handleOpenChange = (open: boolean) => {
    if (!exitConfirmation) {
      openModal('ExitIntent', {
        content:
          'You haven’t finished adding this patient. If you leave now, you will lose your progress.',
        onExitConfirmation: () => {
          setExitConfirmation(true);
          resetForm();
          closeModal('PatientCreate');
        },
      });

      return false;
    }

    if (!open) {
      onModalClose();
    }

    return true;
  };

  return (
    <Modal
      testID={TEST_ID}
      title={getModalTitle(currentStep)}
      hasCloseButton
      size="m"
      primaryAction={{
        label: getNextStepButtonLabel(),
        onPress: () => void goToNextStep(),
        disabled: isNextStepDisabled,
      }}
      secondaryAction={{
        label:
          currentStep === PatientCreateSteps.PATIENT_INTRO ? 'Cancel' : 'Back',
        onPress: goToPreviousStep,
      }}
      onOpenChange={handleOpenChange}
    >
      {isFormLoading && (
        <ContentLoader
          testID={buildTestId({ testID: TEST_ID, suffix: 'loader' })}
        />
      )}

      {currentStep === PatientCreateSteps.PATIENT_INTRO && (
        <PatientIntroForm
          ref={introFormRef}
          defaultValues={values}
          onSubmit={onPatientIntroSubmit}
          setIsNextDisabled={setIsNextStepDisabled}
          testID={buildTestId({ testID: TEST_ID, suffix: ['intro', 'form'] })}
        />
      )}

      {currentStep === PatientCreateSteps.PATIENT_INFORMATION && (
        <PatientInformationForm
          ref={informationFormRef}
          defaultValues={values}
          onSubmit={onPatientInformationSubmit}
          setIsNextDisabled={setIsNextStepDisabled}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['information', 'form'],
          })}
        />
      )}

      {currentStep === PatientCreateSteps.PATIENT_SETTINGS && (
        <PatientSettingsForm
          ref={settingsFormRef}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['settings', 'form'],
          })}
        />
      )}

      {(currentStep === PatientCreateSteps.PATIENT_ALREADY_EXISTS ||
        currentStep ===
          PatientCreateSteps.PATIENT_ANOTHER_DEPARTMENT_ACCOUNT_FOUND ||
        currentStep === PatientCreateSteps.PATIENT_ARCHIVED_ACCOUNT_FOUND) &&
        foundPatient !== undefined && (
          <Stack gap="$4" marginTop="$6">
            <FoundPatientMessage
              step={currentStep}
              patient={foundPatient}
              testID={buildTestId({
                testID: TEST_ID,
                suffix: ['found', 'message'],
              })}
            />
          </Stack>
        )}
    </Modal>
  );
};
